<template>
  <div class="h-full overflow-y-auto">
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin text-xl text-gray-400" />
    </div>

    <!-- Generating in progress (no completed biopsy yet) -->
    <div v-else-if="generating && !biopsy" class="flex items-center justify-center py-12 px-6">
      <div class="text-center">
        <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin text-2xl text-gray-400 mb-3" />
        <p class="text-sm text-gray-500">Generating chart biopsy...</p>
        <p class="text-xs text-gray-400 mt-1">This usually takes 30-60 seconds</p>
      </div>
    </div>

    <!-- No biopsy available -->
    <div v-else-if="!biopsy" class="flex items-center justify-center py-12 px-6">
      <div class="text-center">
        <FontAwesomeIcon :icon="['fas', 'file-medical']" class="text-3xl text-gray-300 mb-3" />
        <p class="text-sm text-gray-400">No chart biopsy available for this patient</p>
        <button
          @click="runBiopsy"
          class="mt-3 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-900 text-white hover:bg-gray-800 flex items-center gap-1.5 mx-auto"
        >
          <FontAwesomeIcon :icon="['fas', 'wand-magic-sparkles']" class="text-[10px]" />
          Generate Chart Biopsy
        </button>
      </div>
    </div>

    <!-- Biopsy Content -->
    <div v-else class="space-y-3 p-4 bg-gray-50">
      <!-- Header with generate + version selector -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-semibold text-gray-900">Chart Biopsy</h3>
          <button
            @click="runBiopsy"
            :disabled="generating"
            class="px-2 py-0.5 text-[10px] font-medium rounded-md transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <FontAwesomeIcon :icon="['fas', generating ? 'spinner' : 'rotate']" :class="{ 'animate-spin': generating }" class="text-[9px]" />
            {{ generating ? 'Running...' : 'Refresh' }}
          </button>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="versions.length > 1"
            :disabled="currentIndex >= versions.length - 1"
            @click="selectVersion(currentIndex + 1)"
            class="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Older version"
          >
            <FontAwesomeIcon :icon="['fas', 'chevron-left']" class="text-[10px]" />
          </button>
          <span class="text-[10px] text-gray-400">
            {{ timeAgo(biopsy.created_at) }}
            <span v-if="isStale" class="ml-1 text-amber-500 cursor-pointer hover:text-amber-600" @click="runBiopsy" title="Data may have changed — click to refresh">
              <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" class="text-[9px]" /> stale
            </span>
            <span v-if="versions.length > 1" class="ml-1 text-gray-300">({{ currentIndex + 1 }}/{{ versions.length }})</span>
          </span>
          <button
            v-if="versions.length > 1"
            :disabled="currentIndex <= 0"
            @click="selectVersion(currentIndex - 1)"
            class="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Newer version"
          >
            <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="text-[10px]" />
          </button>
        </div>
      </div>

      <!-- Patient Snapshot -->
      <div class="bg-white rounded-lg p-3 border border-gray-100">
        <div class="text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-1">Patient Snapshot</div>
        <p class="text-xs text-gray-700 leading-relaxed">{{ data.patient_snapshot }}</p>
      </div>

      <!-- Clinical Priorities -->
      <Section v-if="data.clinical_priorities" title="Clinical Priorities" icon="bullseye" color="red">
        <p class="text-xs text-gray-700 leading-relaxed">{{ data.clinical_priorities }}</p>
      </Section>

      <!-- Red Flags -->
      <Section v-if="data.red_flags" title="Red Flags" icon="triangle-exclamation" color="amber">
        <p class="text-xs text-gray-700 leading-relaxed">{{ data.red_flags }}</p>
      </Section>

      <!-- Active Problems -->
      <Section v-if="data.active_problems" title="Active Problems" icon="stethoscope" color="purple">
        <p class="text-xs text-gray-700 leading-relaxed">{{ data.active_problems }}</p>
      </Section>

      <!-- Recent Activity -->
      <Section v-if="data.recent_activity" title="Recent Activity" icon="calendar-check" color="blue">
        <p class="text-xs text-gray-700 leading-relaxed">{{ data.recent_activity }}</p>
      </Section>

      <!-- Medications -->
      <Section v-if="data.medications || surescriptsByDrug.length || activeEhrMeds.length" title="Medications" icon="pills" color="green">
        <p v-if="data.medications" class="sensitive text-xs text-gray-700 leading-relaxed" data-demo-blur>{{ data.medications }}</p>

        <!-- Structured Active & Recently Filled Medications (#5095) -->
        <div v-if="surescriptsByDrug.length" class="mt-2.5 pt-2.5 border-t border-gray-100">
          <div class="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">
            Active &amp; Recently Filled Medications (Surescripts · {{ surescriptsByDrug.length }})
          </div>
          <div class="divide-y divide-gray-100 border border-gray-200 rounded-md overflow-hidden">
            <div
              v-for="(grp, i) in surescriptsByDrug"
              :key="'ss-' + i"
              class="px-2.5 py-1.5 bg-white flex items-start justify-between gap-2 text-xs"
            >
              <div class="min-w-0 flex-1">
                <div class="font-medium text-gray-900 truncate">
                  {{ grp.drug_name }}
                  <span
                    v-if="grp.dea_schedule"
                    class="ml-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-medium"
                  >C-{{ grp.dea_schedule }}</span>
                </div>
                <div class="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <span v-if="grp.latest.filled_dt">Last fill {{ formatMedDate(grp.latest.filled_dt) }}</span>
                  <span v-if="grp.latest.quantity">· Qty {{ grp.latest.quantity }}</span>
                  <span v-if="grp.latest.days_supply">· {{ grp.latest.days_supply }}d supply</span>
                  <span v-if="grp.fills.length > 1">· {{ grp.fills.length }} fills</span>
                  <span v-if="grp.latest.prescriber_name">· <span class="sensitive" data-spoof-kind="provider">{{ grp.latest.prescriber_name }}</span></span>
                  <span v-if="grp.latest.pharmacy_name">· <span class="sensitive" data-spoof-kind="facility">{{ grp.latest.pharmacy_name }}</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="activeEhrMeds.length" class="mt-2.5 pt-2.5 border-t border-gray-100">
          <div class="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">
            Active EHR Medications ({{ activeEhrMeds.length }})
          </div>
          <div class="divide-y divide-gray-100 border border-gray-200 rounded-md overflow-hidden">
            <div
              v-for="(med, i) in activeEhrMeds"
              :key="'ehr-' + i"
              class="px-2.5 py-1.5 bg-white flex items-start justify-between gap-2 text-xs"
            >
              <div class="min-w-0 flex-1">
                <div class="font-medium text-gray-900 truncate">{{ med.MED_DETAILS || 'Unknown' }}</div>
                <div class="text-[10px] text-gray-500 mt-0.5 space-x-2">
                  <span v-if="med.STATUS">{{ med.STATUS }}</span>
                  <span v-if="med.START_DATE">Since {{ formatMedDate(med.START_DATE) }}</span>
                  <span v-if="med.PRACTITIONER_NAME">Prescriber <span class="sensitive" data-spoof-kind="provider">{{ med.PRACTITIONER_NAME }}</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <!-- Labs and Vitals -->
      <Section v-if="data.labs_and_vitals" title="Labs & Vitals" icon="flask" color="teal">
        <p class="text-xs text-gray-700 leading-relaxed">{{ data.labs_and_vitals }}</p>
      </Section>

      <!-- Data Gaps -->
      <Section v-if="data.data_gaps" title="Data Gaps" icon="circle-question" color="gray">
        <p class="text-xs text-gray-500 leading-relaxed">{{ data.data_gaps }}</p>
      </Section>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import axios from 'axios';
import { formatCalendarDate } from '@/utils/common';

const Section = {
  name: 'BiopsySection',
  components: { FontAwesomeIcon },
  props: {
    title: String,
    icon: String,
    color: { type: String, default: 'gray' }
  },
  computed: {
    iconColor() {
      const map = {
        red: 'text-red-500',
        amber: 'text-amber-500',
        purple: 'text-purple-500',
        blue: 'text-blue-500',
        green: 'text-green-500',
        teal: 'text-teal-500',
        indigo: 'text-indigo-500',
        gray: 'text-gray-400'
      };
      return map[this.color] || map.gray;
    }
  },
  template: `
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
        <FontAwesomeIcon :icon="['fas', icon]" class="text-[10px]" :class="iconColor" />
        <span class="text-[10px] uppercase tracking-wider font-semibold text-gray-600">{{ title }}</span>
      </div>
      <div class="px-3 py-2">
        <slot />
      </div>
    </div>
  `
};

export default {
  name: 'Overview_Biopsy',
  components: { FontAwesomeIcon, Section },

  props: {
    patientIdentifier: { type: String, required: true },
    entityId: { type: Number, default: null },
    medications: { type: Array, default: () => [] },
    surescriptsFills: { type: Array, default: () => [] },
  },

  data() {
    return {
      loading: false,
      generating: false,
      versions: [],
      currentIndex: 0,
      pollTimer: null,
    };
  },

  computed: {
    biopsy() {
      return this.versions[this.currentIndex] || null;
    },
    data() {
      return this.biopsy?.data || {};
    },
    isStale() {
      if (!this.biopsy?.created_at) return false;
      const age = Date.now() - new Date(this.biopsy.created_at).getTime();
      return age > 24 * 60 * 60 * 1000;
    },
    surescriptsByDrug() {
      const map = new Map();
      for (const f of (this.surescriptsFills || [])) {
        const key = f.drug_name || 'Unknown medication';
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(f);
      }
      return [...map.entries()]
        .map(([drug_name, fills]) => ({
          drug_name,
          fills,
          latest: fills[0] || {},
          dea_schedule: fills.find(x => x.dea_schedule)?.dea_schedule || null,
        }))
        .sort((a, b) => (b.latest.filled_dt || '').localeCompare(a.latest.filled_dt || ''));
    },
    activeEhrMeds() {
      return [...(this.medications || [])].sort((a, b) =>
        (b.START_DATE || '').localeCompare(a.START_DATE || '')
      ).slice(0, 15);
    },
  },

  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },

  watch: {
    entityId: {
      immediate: true,
      handler(val) {
        if (val) this.fetchBiopsies();
      }
    }
  },

  methods: {
    async fetchBiopsies() {
      if (!this.entityId) return;
      this.loading = true;
      try {
        const { data } = await axios.get('/api/hermes/subject-results/', {
          params: { template_name: 'Chart Biopsy', entity: this.entityId }
        });
        this.versions = data.results;
        this.currentIndex = 0;

        const pending = data.pending;
        if (pending && !this.pollTimer) {
          this.generating = true;
          this.pollTimer = setInterval(async () => {
            await this.fetchBiopsies();
          }, 5000);
        } else if (!pending && this.pollTimer) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
          this.generating = false;
        }
      } catch (e) {
        console.error('Failed to fetch chart biopsies:', e);
        this.versions = [];
      } finally {
        this.loading = false;
      }
    },

    async runBiopsy() {
      if (!this.entityId || this.generating) return;
      this.generating = true;
      try {
        await axios.post('/api/hermes/subject-results/start/', {
          template_name: 'Chart Biopsy',
          entity: this.entityId,
        });
        await this.fetchBiopsies();
      } catch (e) {
        console.error('Failed to trigger chart biopsy:', e);
        this.generating = false;
      }
    },

    selectVersion(index) {
      if (index >= 0 && index < this.versions.length) {
        this.currentIndex = index;
      }
    },

    timeAgo(dateStr) {
      if (!dateStr) return '';
      const diff = Date.now() - new Date(dateStr).getTime();
      const hours = Math.floor(diff / 3600000);
      if (hours < 1) return 'just now';
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days === 1) return 'yesterday';
      if (days < 7) return `${days}d ago`;
      return `${Math.floor(days / 7)}w ago`;
    },

    formatMedDate(dateStr) {
      if (!dateStr) return '';
      return formatCalendarDate(dateStr, 'en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    },
  }
};
</script>
